/** @jsxImportSource @emotion/react */
import classNames from 'classnames';
import React, { type ReactElement, useMemo } from 'react';
import { useIsAdmin } from 'shared/hooks/use-is-admin';
import UDNavLink from '../../../link/nav-link';
import UDTippy from '../../../tippy';
import { UDText } from 'ud-ui/components';
import { theme } from 'styles/theme/theme';
import { useMatch } from 'react-router-dom';

interface IProps {
  to?: string;
  checkPath?: string;
  icon: ReactElement;
  label: string;
  tooltip?: string;
  disabled?: boolean;
  onClick?: () => void;
  adminOnly?: boolean;
  hide?: boolean;
  badge?: {
    label?: string;
    icon?: ReactElement;
    color?: string;
  };
}

function MenuItemIcon(props: IProps) {
  const { to, checkPath, icon, label, tooltip, disabled, onClick, adminOnly, badge, hide } = props;
  const isAdmin = useIsAdmin();

  const url = checkPath ?? (typeof to === 'string' ? to : '');
  const isActive = useMatch(url ?? '');

  const component = useMemo(() => {
    if (!to) {
      return (
        <div
          onClick={onClick}
          style={{ height: 36, cursor: 'pointer' }}
          className={classNames('link secondary text d-flex align-items-center text subhead px-10', {
            'gap-2': !!icon,
            active: isActive,
          })}
        >
          <div className={'flex-center'}>{icon}</div>
          {label}
        </div>
      );
    }

    return (
      <UDNavLink
        to={to}
        checkPath={checkPath ?? to + '/*'}
        style={{ height: 36 }}
        className={classNames('nav-item d-flex align-items-center text subhead px-10', {
          'gap-2': !!icon,
        })}
        variant={'secondary'}
        disabled={disabled}
      >
        <div className={'flex-center'}>{icon}</div>
        {label}
        {badge && (
          <div
            style={{
              minWidth: 20,
              height: 20,
              borderRadius: 4,
              backgroundColor: badge.color ?? theme.colors.Blue,
            }}
            className="flex-center"
          >
            {badge.icon && (badge.icon as ReactElement)}
            {badge.label && (
              <UDText type={'text-1'} isNew color={theme.colors.DarkYellow}>
                {badge.label}
              </UDText>
            )}
          </div>
        )}
      </UDNavLink>
    );
  }, [badge, checkPath, disabled, icon, label, onClick, to]);

  if ((adminOnly && !isAdmin) || hide) {
    return null;
  }

  if (!tooltip) {
    return component;
  }

  return (
    <UDTippy content={tooltip} placement={'bottom'}>
      <div>{component}</div>
    </UDTippy>
  );
}

export default MenuItemIcon;
